import React, { createContext, useContext, useState } from "react"
import type { ChatFocusContext } from "@/lib/chat/focus-context"

interface ChatDialogContextType {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  focusContext: ChatFocusContext | null
  setFocusContext: (context: ChatFocusContext | null) => void
  openWithContext: (context: ChatFocusContext | null) => void
  pendingMessage: string | null
  clearPendingMessage: () => void
  openWithMessage: (message: string) => void
}

const ChatDialogContext = createContext<ChatDialogContextType | undefined>(
  undefined
)

export function ChatDialogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [focusContext, setFocusContext] = useState<ChatFocusContext | null>(
    null
  )
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const openWithContext = (context: ChatFocusContext | null) => {
    setFocusContext(context)
    setIsOpen(true)
  }

  const clearPendingMessage = () => setPendingMessage(null)

  const openWithMessage = (message: string) => {
    setPendingMessage(message)
    setIsOpen(true)
  }

  return (
    <ChatDialogContext.Provider
      value={{
        isOpen,
        setIsOpen,
        focusContext,
        setFocusContext,
        openWithContext,
        pendingMessage,
        clearPendingMessage,
        openWithMessage,
      }}
    >
      {children}
    </ChatDialogContext.Provider>
  )
}

export function useChatSheetState() {
  const context = useContext(ChatDialogContext)
  if (!context) {
    throw new Error(`useChatSheetState must be used within ChatDialogProvider`)
  }
  return context
}
